import { CartEntrySummary } from "./CartEntrySummary";
import { CartEntryDetails } from "./CartEntryDetails";
import type { GroupedCartEntry, LineItem, PlainLineItem } from "~/features/cart/cart/models/CartData";
import { Card } from "~/common/mui";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { CheckoutRegularProductLine } from "./CheckoutRegularProductLine";
import { useSelector } from "react-redux";
import { compareRGCEEquality, selectResolvedGroupedEntry } from "~/features/cart/cart/utils/productEntryHelper";
import { LineItemComponent } from "~/features/cart/cartV2/RegularProductLine";
import { useEffect, useRef } from "react";
import { isKITProduct } from "~/domains/product/types/ProductData";
import { KitProductLine } from "./KitProductLine";
import { CheckoutKitProductLine } from "./CheckoutKitProductLine";
import { useAlternateProducts } from "~/features/cart/cart/AlternateProductsContext";

interface CartEntryProps {
  groupingEntry: GroupedCartEntry;
  editMode: boolean;
  onRender: (element: Element) => void;
  onDelete: (productCode: string, designation: string) => void;
}

export const CartEntry = ({ groupingEntry, editMode, onRender, onDelete }: CartEntryProps) => {
  const resolvedEntry = useSelector(selectResolvedGroupedEntry(groupingEntry), compareRGCEEquality);
  const { setScrollIndex } = useAlternateProducts();

  // Assuming the first descriptor corresponds to the main product for the summary
  // In a grouped entry, there might be multiple descriptors, but usually they relate to the same base product or variants
  const mainDescriptor = resolvedEntry.descriptors[0];

  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = rootRef.current;
    if (!element || !onRender) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      onRender(element);
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [onRender]);

  if (!mainDescriptor) {
    return null;
  }

  const handleDelete = () => {
    const productCode = mainDescriptor.base.code;
    const designation = mainDescriptor.base.designation;
    if (resolvedEntry.cartEntry.packEntryIds.length > 1) {
      setScrollIndex(-1);
      return;
    }
    onDelete(productCode, designation);
  };

  let renderItem;
  if (editMode) {
    renderItem = (lineItem: LineItem, product: ProductDescriptor) => {
      if (isKITProduct(product)) {
        return (
          <KitProductLine
            key={lineItem.pk}
            lineItem={lineItem as PlainLineItem}
            product={product}
            onDelete={handleDelete}
          />
        );
      }
      return <LineItemComponent key={lineItem.pk} lineItem={lineItem} product={product} onDelete={handleDelete} />;
    };
  } else {
    renderItem = (lineItem: LineItem, product: ProductDescriptor) => {
      if (isKITProduct(product)) {
        return <CheckoutKitProductLine key={lineItem.pk} lineItem={lineItem as PlainLineItem} product={product} />;
      }
      return <CheckoutRegularProductLine key={lineItem.pk} lineItem={lineItem} product={product} />;
    };
  }

  return (
    <Card ref={rootRef}>
      <CartEntrySummary
        product={mainDescriptor}
        resolvedCartEntry={resolvedEntry}
        editMode={editMode}
        onDelete={handleDelete}
      />
      <CartEntryDetails
        packGroups={resolvedEntry.packGroups}
        product={mainDescriptor}
        renderItem={renderItem}
        editMode={editMode}
        initiallyExpanded={true}
      />
    </Card>
  );
};
